import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Mail, ArrowRight, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { workspaceApi } from '../services/api';
import './AcceptInvite.css';

function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState(null);
    const [error, setError] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchInvitationDetails();
    }, [token]);

    const fetchInvitationDetails = async () => {
        try {
            const response = await workspaceApi.getInviteDetails(token);
            setInvitation(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid or expired invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await workspaceApi.acceptInvite(token);
            setSuccess(true);
            setTimeout(() => {
                navigate('/', { state: { message: 'Successfully joined the workspace!' } });
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to accept invitation');
            setAccepting(false);
        }
    };

    const handleDecline = async () => {
        if (!window.confirm('Are you sure you want to decline this invitation?')) return;

        try {
            await workspaceApi.declineInvite(token);
            navigate('/login', { state: { message: 'Invitation declined.' } });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to decline invitation');
        }
    };

    const handleSignup = () => {
        // Persist invitation state for OAuth/Reloads
        localStorage.setItem('pendingInvitation', JSON.stringify({
            token,
            returnTo: `/invite/${token}`
        }));

        navigate('/signup', {
            state: {
                returnTo: `/invite/${token}`,
                email: invitation.email,
                message: 'Create an account to accept the invitation'
            }
        });
    };

    const handleLogin = () => {
        // Persist invitation state for OAuth/Reloads
        localStorage.setItem('pendingInvitation', JSON.stringify({
            token,
            returnTo: `/invite/${token}`
        }));

        navigate('/login', {
            state: {
                returnTo: `/invite/${token}`,
                email: invitation.email,
                message: 'Log in to accept the invitation'
            }
        });
    };

    if (loading) {
        return (
            <div className="invite-page">
                <div className="invite-card">
                    <Loader className="spinner" size={40} />
                    <p>Loading invitation...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="invite-page">
                <div className="invite-card error">
                    <AlertCircle size={48} />
                    <h1>Invalid Invitation</h1>
                    <p>{error}</p>
                    <Link to="/login" className="btn-primary">
                        Go to Login
                    </Link>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="invite-page">
                <div className="invite-card success">
                    <CheckCircle size={48} />
                    <h1>Invitation Accepted!</h1>
                    <p>Redirecting you to your workspace...</p>
                </div>
            </div>
        );
    }

    // Check if authenticated user's email matches invitation
    const emailMismatch = isAuthenticated && user?.email?.toLowerCase() !== invitation.email.toLowerCase();

    return (
        <div className="invite-page">
            <div className="invite-card">
                <div className="invite-icon">
                    <Mail size={32} />
                </div>
                <h1>You're Invited!</h1>
                <p className="invite-subtitle">
                    You've been invited to join <strong>{invitation.workspace?.name}</strong>
                </p>

                <div className="invite-details">
                    <div className="detail-row">
                        <span className="detail-label">Workspace</span>
                        <span className="detail-value">{invitation.workspace?.name}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Role</span>
                        <span className={`role-badge ${invitation.role}`}>
                            {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Invited by</span>
                        <span className="detail-value">{invitation.invitedBy}</span>
                    </div>
                    {invitation.message && (
                        <div className="invite-message">
                            <p>"{invitation.message}"</p>
                        </div>
                    )}
                </div>

                {emailMismatch && (
                    <div className="invite-error">
                        <AlertCircle size={18} />
                        <div>
                            <strong>Email Mismatch</strong>
                            <p>This invitation was sent to {invitation.email}, but you're logged in as {user.email}. Please log out and sign in with the invited email.</p>
                        </div>
                    </div>
                )}

                {!isAuthenticated ? (
                    // Unauthenticated user - show signup/login options
                    <>
                        <div className="invite-actions">
                            <button
                                className="btn-primary"
                                onClick={handleSignup}
                            >
                                <UserPlus size={18} />
                                {invitation.hasAccount ? 'Log In to Accept' : 'Sign Up to Accept'}
                            </button>
                            {invitation.hasAccount && (
                                <button
                                    className="btn-ghost"
                                    onClick={handleLogin}
                                >
                                    <LogIn size={18} />
                                    Already have an account? Log In
                                </button>
                            )}
                        </div>
                        <p className="invite-note">
                            {invitation.hasAccount
                                ? 'You already have an account. Log in to accept this invitation.'
                                : 'Create an account to join this workspace.'}
                        </p>
                    </>
                ) : (
                    // Authenticated user - show accept/decline
                    <>
                        <div className="invite-actions">
                            <button
                                className="btn-primary"
                                onClick={handleAccept}
                                disabled={accepting || emailMismatch}
                            >
                                {accepting ? 'Accepting...' : 'Accept Invitation'}
                                {!accepting && <ArrowRight size={18} />}
                            </button>
                            <button
                                className="btn-ghost"
                                onClick={handleDecline}
                                disabled={accepting}
                            >
                                Decline
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AcceptInvite;
